/**
 * Variant editor of a product (up to 250 rows): grouped by the first option, groups collapsed over 30 variants so
 * collapsed rows are not rendered at all. Rows are memoized; only the edited row re-renders.
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Input, InputNumber, Modal, Select, Space, Typography, message } from 'antd';
import { DownOutlined, RightOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { currencyDigits, fromMinor, money, toMinor } from './common';
import { MoneyInput } from './ShopCommon';

const { Text } = Typography;

export const MAX_VARIANTS = 250;
const COLLAPSE_OVER = 30;
const PAGE = 60; // rows of an ungrouped list / an open group before "Show all"

// weight: stored in grams, shown in the shop's unit
const UNIT = { g: 1, kg: 1000, lb: 453.59237, oz: 28.349523125 };
export const toUnit = (g, u) => (g ? +(Number(g) / (UNIT[u] || 1000)).toFixed(3) : 0);
export const toGrams = (v, u) => Math.round((Number(v) || 0) * (UNIT[u] || 1000));
export const sig = (opts) => (opts || []).join('\u001f');

const GRID = '22px minmax(100px,1fr) 88px 88px 116px 68px 26px';
const CSS = `
.yc-vr{display:grid;grid-template-columns:${GRID};gap:8px;align-items:center;padding:4px 8px;border-bottom:1px solid var(--ant-color-border-secondary,#f0f0f0);font-size:13px}
.yc-vr .nm{font-weight:600;overflow:hidden;white-space:nowrap;text-overflow:ellipsis}
.yc-vr input[type=text]{width:100%;height:24px;padding:0 7px;font:inherit;color:inherit;background:var(--ant-color-bg-container,#fff);border:1px solid var(--ant-color-border,#d9d9d9);border-radius:4px;outline:0}
.yc-vr input[type=text]:focus{border-color:#2357ff;box-shadow:0 0 0 2px rgba(35,87,255,.15)}
.yc-vr button{border:0;background:none;cursor:pointer;color:inherit;height:24px;border-radius:4px;padding:0}
.yc-vr button.on{color:#2357ff}
`;
const rowStyle = { display: 'grid', gridTemplateColumns: GRID, gap: 8, alignItems: 'center', padding: '4px 8px', borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)' };

/** Native text input holding its own text; the page only hears about it on blur/Enter, so typing touches one row. */
const Cell = memo(({ value, parse, format, inputMode, label }) => {
    const shown = format ? format(value) : String(value ?? '');
    const [text, setText] = useState(shown);
    useEffect(() => { setText(shown); }, [shown]);
    const commit = () => {
        parse(text);
        setText(shown); // a changed value comes back through the prop, formatted; a bad one is dropped
    };
    return <input type="text" inputMode={inputMode} aria-label={label} value={text} onChange={(e) => setText(e.target.value)} onFocus={(e) => e.target.select()} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />;
});

const Row = memo(({ v, i, selected, expanded, more, label, currency, labels, onPatch, onSelect, onExpand, renderMore }) => {
    const k = sig(v.options);
    const fmtMoney = (x) => (x === null || x === undefined ? '' : Number(fromMinor(x, currency)).toFixed(currencyDigits(currency)));
    const parseMoney = (field, nullable) => (s) => {
        const txt = String(s).trim().replace(',', '.');
        if (txt === '') { if (nullable || field === 'price') { onPatchIf(field, nullable ? null : 0); return null; } return undefined; }
        const n = Number(txt);
        if (!Number.isFinite(n) || n < 0) return undefined;
        onPatchIf(field, toMinor(n, currency));
        return n;
    };
    const onPatchIf = (field, val) => { if (val !== (v[field] ?? null)) onPatch([i], { [field]: val }); };
    return (
        <>
            <div className="yc-vr" data-variant-row={k}>
                <input type="checkbox" checked={selected} onChange={(e) => onSelect(k, e.target.checked)} aria-label={label} />
                <span className="nm" title={label}>{label}</span>
                <Cell value={v.price} format={fmtMoney} parse={parseMoney('price', false)} inputMode="decimal" label={labels.price} />
                <Cell value={v.compare_at_price} format={fmtMoney} parse={parseMoney('compare_at_price', true)} inputMode="decimal" label={labels.compare} />
                <Cell value={v.sku} label={labels.sku} parse={(s) => { if (s !== (v.sku ?? '')) onPatch([i], { sku: s }); return s; }} />
                {v.track_inventory
                    ? <Cell value={v.inventory_quantity} label={labels.inventory} inputMode="numeric" parse={(s) => {
                        if (!/^-?\d+$/.test(String(s).trim())) return undefined;
                        const n = Number(s);
                        if (n !== Number(v.inventory_quantity)) onPatch([i], { inventory_quantity: n });
                        return n;
                    }} />
                    : <span style={{ textAlign: 'center', opacity: 0.6 }}>∞</span>}
                <button type="button" className={expanded ? 'on' : ''} aria-label={labels.more} title={labels.more} onClick={() => onExpand(k)}><SettingOutlined /></button>
            </div>
            {expanded && <div style={{ padding: 12, background: 'var(--ant-color-fill-quaternary, #fafafa)' }}>{renderMore(v, i, more)}</div>}
        </>
    );
});

/** field + value of a bulk action; value in display units until applied */
const useBulkField = (currency, unit) => {
    const { t } = useTranslation();
    const fields = [
        { value: 'price', label: t('Price') }, { value: 'compare_at_price', label: t('Compare-at') },
        { value: 'inventory_quantity', label: t('Inventory') }, { value: 'sku', label: t('SKU') },
        { value: 'weight', label: `${t('Weight', { context: 'shop' })} (${unit})` },
    ];
    const [field, setField] = useState('price');
    const [val, setVal] = useState(null);
    const input = (extra = {}) => {
        if (field === 'price' || field === 'compare_at_price') return <MoneyInput size="small" addonBefore={null} currency={currency} value={val} onChange={setVal} style={{ width: 110 }} placeholder={t('Value')} {...extra} />;
        if (field === 'sku') return <Input size="small" value={val ?? ''} onChange={(e) => setVal(e.target.value)} style={{ width: 130 }} placeholder={t('Value')} {...extra} />;
        return <InputNumber size="small" min={field === 'weight' ? 0 : undefined} precision={field === 'weight' ? undefined : 0} value={val} onChange={setVal} style={{ width: 100 }} placeholder={t('Value')} {...extra} />;
    };
    const select = <Select size="small" value={field} onChange={(f) => { setField(f); setVal(null); }} options={fields} style={{ width: 130 }} popupMatchSelectWidth={false} />;
    // compare-at may be emptied; every other field needs a value
    const ready = field === 'compare_at_price' || (val !== null && val !== '');
    const patch = () => {
        if (field === 'weight') return { weight: toGrams(val, unit) };
        if (field === 'price') return { price: val ?? 0 };
        if (field === 'inventory_quantity') return { inventory_quantity: val ?? 0 };
        if (field === 'sku') return { sku: val ?? '' };
        return { compare_at_price: val ?? null };
    };
    return { select, input, ready, patch };
};

const ByOption = ({ options, variants, currency, unit, onPatch }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [oi, setOi] = useState(0);
    const [value, setValue] = useState();
    const bulk = useBulkField(currency, unit);
    const opts = options.filter((o) => o.name && o.values?.length);
    const matching = useMemo(() => (value === undefined ? [] : variants.reduce((a, v, i) => { if (v.options?.[oi] === value) a.push(i); return a; }, [])), [variants, oi, value]);
    const apply = () => {
        onPatch(matching, bulk.patch());
        message.success(t('Applied to {{n}} variants', { n: matching.length }));
        setOpen(false);
    };
    return (
        <>
            <Button size="small" onClick={() => setOpen(true)}>{t('Apply by option')}</Button>
            <Modal open={open} title={t('Apply to all with option value')} onCancel={() => setOpen(false)} onOk={apply} destroyOnHidden width={420}
                okText={matching.length ? t('Apply to {{n}} variants', { n: matching.length }) : t('Apply')} okButtonProps={{ disabled: !matching.length || !bulk.ready }}>
                <Space wrap>
                    <Select size="small" value={oi} onChange={(x) => { setOi(x); setValue(undefined); }} style={{ width: 130 }} options={opts.map((o, i) => ({ value: i, label: o.name }))} />
                    <Select size="small" value={value} onChange={setValue} style={{ width: 150 }} placeholder={t('Value')} showSearch options={(opts[oi]?.values || []).map((x) => ({ value: x, label: x }))} />
                </Space>
                <div style={{ marginTop: 8 }}><Space wrap>{bulk.select}{bulk.input()}</Space></div>
            </Modal>
        </>
    );
};

const SelectionBar = ({ keys, indexOf, currency, unit, onPatch, onClear }) => {
    const { t } = useTranslation();
    const bulk = useBulkField(currency, unit);
    const apply = () => {
        onPatch(keys.map(indexOf).filter((i) => i >= 0), bulk.patch());
        message.success(t('Applied to {{n}} variants', { n: keys.length }));
    };
    return (
        <div style={{ position: 'sticky', top: 64, zIndex: 3, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '6px 8px', background: 'var(--ant-color-primary-bg, #e6f4ff)', borderRadius: 6, marginBottom: 8 }}>
            <Text strong style={{ fontSize: 13 }}>{t('{{n}} selected', { n: keys.length })}</Text>
            {bulk.select}
            {bulk.input({ onPressEnter: () => bulk.ready && apply() })}
            <Button size="small" type="primary" disabled={!bulk.ready} onClick={apply}>{t('Apply')}</Button>
            <Button size="small" type="text" onClick={onClear}>{t('Clear')}</Button>
        </div>
    );
};

const ProductVariants = ({ options, variants, images, currency, unit, onPatch, renderMore }) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState(() => new Set());
    const [expanded, setExpanded] = useState(() => new Set());
    const [openMap, setOpenMap] = useState({});
    const [limits, setLimits] = useState({});

    // handlers stay stable so memoized rows skip re-rendering; they always call the page's latest functions
    const latest = useRef({ onPatch, renderMore });
    latest.current = { onPatch, renderMore };
    const patch = useCallback((idx, p) => latest.current.onPatch(idx, p), []);
    const more = useCallback((v, i) => latest.current.renderMore(v, i), []);
    const onSelect = useCallback((k, on) => setSelected((s) => { const n = new Set(s); if (on) n.add(k); else n.delete(k); return n; }), []);
    const onExpand = useCallback((k) => setExpanded((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; }), []);

    const named = options.filter((o) => o.name && o.values?.length);
    const grouped = named.length > 1 && (named[0].values.length > 1);
    const defaultOpen = variants.length <= COLLAPSE_OVER;
    const groups = useMemo(() => {
        const map = new Map();
        variants.forEach((v, i) => {
            const g = grouped ? (v.options?.[0] ?? '') : '';
            if (!map.has(g)) map.set(g, []);
            map.get(g).push({ v, i });
        });
        return [...map.entries()].map(([name, rows]) => {
            const prices = rows.map((r) => Number(r.v.price) || 0);
            return {
                name, rows, keys: rows.map((r) => sig(r.v.options)), min: Math.min(...prices), max: Math.max(...prices),
                stock: rows.reduce((a, r) => a + (r.v.track_inventory ? Number(r.v.inventory_quantity) || 0 : 0), 0),
            };
        });
    }, [variants, grouped]);

    const allKeys = useMemo(() => variants.map((v) => sig(v.options)), [variants]);
    const keyIndex = useMemo(() => new Map(allKeys.map((k, i) => [k, i])), [allKeys]);
    const liveSelected = allKeys.filter((k) => selected.has(k)); // a removed option value drops out of the selection
    const combos = named.reduce((a, o) => a * o.values.length, 1);
    const setMany = (keys, on) => setSelected((s) => { const n = new Set(s); keys.forEach((k) => { if (on) n.add(k); else n.delete(k); }); return n; });

    const labels = useMemo(() => ({ price: t('Price'), compare: t('Compare-at'), sku: t('SKU'), inventory: t('Inventory'), more: t('More') }), [t]);

    const renderRows = (g) => {
        const limit = limits[g.name] || PAGE;
        return (
            <>
                {g.rows.slice(0, limit).map(({ v, i }) => {
                    const k = sig(v.options);
                    const exp = expanded.has(k);
                    return (
                        <Row key={k} v={v} i={i} selected={selected.has(k)} expanded={exp} more={exp ? images : null}
                            label={(grouped ? (v.options || []).slice(1) : (v.options || [])).join(' / ') || v.title} currency={currency} labels={labels}
                            onPatch={patch} onSelect={onSelect} onExpand={onExpand} renderMore={more} />
                    );
                })}
                {g.rows.length > limit && (
                    <div style={{ padding: 6, textAlign: 'center' }}>
                        <Button size="small" type="link" onClick={() => setLimits((l) => ({ ...l, [g.name]: g.rows.length }))}>{t('Show all {{n}}', { n: g.rows.length })}</Button>
                    </div>
                )}
            </>
        );
    };

    return (
        <div>
            <style>{CSS}</style>
            {liveSelected.length > 0 && (
                <SelectionBar keys={liveSelected} indexOf={(k) => (keyIndex.has(k) ? keyIndex.get(k) : -1)} currency={currency} unit={unit} onPatch={patch} onClear={() => setSelected(new Set())} />
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <Checkbox checked={liveSelected.length === allKeys.length && allKeys.length > 0} indeterminate={liveSelected.length > 0 && liveSelected.length < allKeys.length}
                    onChange={(e) => setMany(allKeys, e.target.checked)}>{t('Select all')}</Checkbox>
                <ByOption options={options} variants={variants} currency={currency} unit={unit} onPatch={patch} />
                {grouped && (
                    <>
                        <Button size="small" type="text" onClick={() => setOpenMap(Object.fromEntries(groups.map((g) => [g.name, true])))}>{t('Expand all')}</Button>
                        <Button size="small" type="text" onClick={() => setOpenMap(Object.fromEntries(groups.map((g) => [g.name, false])))}>{t('Collapse all')}</Button>
                    </>
                )}
                <Text type="secondary" style={{ fontSize: 12, marginInlineStart: 'auto' }}>{t('{{n}} variants', { n: variants.length })}</Text>
            </div>
            {combos > MAX_VARIANTS && <div style={{ marginBottom: 8 }}><Text type="warning" style={{ fontSize: 12 }}>{t('Only the first {{n}} combinations are kept', { n: MAX_VARIANTS })}</Text></div>}
            <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: 560 }}>
                    <div style={{ ...rowStyle, fontSize: 12, color: 'var(--ant-color-text-tertiary, #888)' }}>
                        <span /><span>{t('Variant')}</span><span>{t('Price')} ({currency})</span><span>{t('Compare-at')}</span><span>{t('SKU')}</span><span>{t('Inventory')}</span><span />
                    </div>
                    {groups.map((g) => {
                        if (!grouped) return <div key="all">{renderRows(g)}</div>;
                        const open = openMap[g.name] ?? defaultOpen;
                        const nSel = g.keys.filter((k) => selected.has(k)).length;
                        return (
                            <div key={g.name} data-variant-group={g.name}>
                                <div style={{ ...rowStyle, background: 'var(--ant-color-fill-quaternary, #fafafa)', cursor: 'pointer', userSelect: 'none', gridTemplateColumns: '22px 1fr auto' }}
                                    onClick={() => setOpenMap((m) => ({ ...m, [g.name]: !open }))}>
                                    <span onClick={(e) => e.stopPropagation()}>
                                        <Checkbox checked={nSel === g.keys.length} indeterminate={nSel > 0 && nSel < g.keys.length} onChange={(e) => setMany(g.keys, e.target.checked)} />
                                    </span>
                                    <Text strong>{open ? <DownOutlined style={{ fontSize: 10, marginInlineEnd: 6 }} /> : <RightOutlined style={{ fontSize: 10, marginInlineEnd: 6 }} />}{g.name}</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {t('{{n}} variants', { n: g.rows.length })} · {g.min === g.max ? money(g.min, currency) : `${money(g.min, currency)} – ${money(g.max, currency)}`} · {t('{{n}} in stock', { n: g.stock })}
                                    </Text>
                                </div>
                                {open && renderRows(g)}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ProductVariants;
